import {
  ActionFunctionArgs,
  Form,
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
} from "react-router-dom";
import toast from "react-hot-toast";
import { Input } from "../../components/inputs/Input";
import TicketingItemHTML from "../tickets/components/TickingItemHTML";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import {
  organizationStore,
  useOrganizationStore,
} from "../../store/organization";
import { DatePicker } from "../../components/DatePicker";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_EVENT } from "./queries";
import { CREATE_EVENT } from "./mutation";
import { Event } from "../../types/event";
import {
  GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
  GET_LIST_VENDORS_BY_ORGANIZATION,
} from "../../queries";
import { PaymentProcessor } from "../../types/paymentProcessor";
import { Vendor } from "../../types/vendor";
import { parseDate } from "@internationalized/date";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { TagsInput } from "../../components/inputs/TagsInput";
import { Fragment, useRef, useState } from "react";
import PercentageInput from "../../components/inputs/PercentageInput";
import { userStore } from "../../store/user";
import {Tab} from "../../components/Tab";
import PriceInput from "../../components/inputs/PriceInput";
import CompactThumbUploader from "../../components/CompactThumbUploader";
import DemographicQuestionsBuilder from "../../components/DemographicQuestionsBuilder";
import SubEventManager, { SubEventManagerHandle } from "../../components/SubEventManager";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { GET_IMAGE_LIBRARY_ITEMS_LIMITED } from "../image-library/queries";
import { ImageLibrary } from "../../types/item";
import {
  REMAINING_DUE_BUTTON_IDS,
  REMAINING_DUE_BUTTON_DISPLAY_NAMES,
  mergeRemainingDueButtons,
  type RemainingDueButtonId,
  type RemainingDueButtons,
} from "./remainingDueButtonsConfig";
type PaymentType = "card" | "cash" | "rfid" | "qr";
const client = createApolloClient();

const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

export const eventCloneLoader = async ({ params }: LoaderFunctionArgs) => {
  const organizationId = getState().organizationId;
  const { id } = params;
  const [
    { data: eventData },
    { data: paymentProcessors },
    { data: vendorData },
    { data: imageData },
  ] = await Promise.all([
    client.query<{ events_by_pk: Event }>({
      query: GET_EVENT,
      variables: { id },
    }),
    client.query({
      query: GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
      variables: {
        where: organizationId
          ? {
              organization_id: {
                _eq: organizationId,
              },
            }
          : {},
      },
      fetchPolicy: "network-only",
    }),
    client.query({
      query: GET_LIST_VENDORS_BY_ORGANIZATION,
      variables: {
        where: organizationId
          ? {
              organization_id: {
                _eq: organizationId,
              },
            }
          : {},
      },
    }),
    client.query({
      query: GET_IMAGE_LIBRARY_ITEMS_LIMITED,
      variables: {
        where: organizationId
          ? {
              organization_id: {
                _eq: organizationId,
              },
              is_active: {
                _eq: true,
              },
            }
          : {},
      },
    }),
  ]);
  return {
    event: eventData.events_by_pk,
    paymentProcessors: paymentProcessors.payment_processor_config,
    vendors: vendorData.vendors,
    images: imageData.image_library,
  };
};

export const eventCloneAction = async ({
  params,
  request,
}: ActionFunctionArgs) => {
  const { id } = params;
  const user = getUserStore().user;

  const body = await request.formData();
  let timezonediffs = 0;
  const paymentTypes: PaymentType[] = body.getAll(
    "payment_types"
  ) as PaymentType[];
  body.delete("payment_types");
  body.delete("image_library");
  const signupQuestionsRaw = body.get("signup_questions") as string;
  const signup_questions = signupQuestionsRaw ? JSON.parse(signupQuestionsRaw) : null;
  body.delete("signup_questions");
  const subEventsRaw = body.get("sub_events") as string;
  const sub_events = subEventsRaw ? JSON.parse(subEventsRaw) : null;
  body.delete("sub_events");
  const is_asset_transfer_combine = body.get("is_asset_transfer_combine") === "true";
  body.delete("is_asset_transfer_combine");
  const showRemainingCta = body.get("ticketing_show_remaining_cta") === "true";
  body.delete("ticketing_show_remaining_cta");
  const ticketing_configurations = { show_remaining_cta: showRemainingCta };
  const pinOnAssociationOnly = body.get("cashless_pin_on_association_only") === "true";
  body.delete("cashless_pin_on_association_only");
  // Lets the event stay active in the dashboard while being hidden from the
  // tablet's event-picker dropdown so cashiers don't accidentally select it.
  const hideFromPosDropdown = body.get("cashless_hide_from_pos_dropdown") === "true";
  body.delete("cashless_hide_from_pos_dropdown");
  const registrationHeaderRaw = body.get("cashless_registration_header");
  body.delete("cashless_registration_header");
  // Remaining Due button configs — see EventEdit.tsx for the full rationale.
  const remainingDueButtonsRaw = body.get("cashless_remaining_due_buttons") as string | null;
  body.delete("cashless_remaining_due_buttons");
  for (const id of REMAINING_DUE_BUTTON_IDS) {
    body.delete(`remaining_due_btn_${id}_label`);
  }
  let remaining_due_buttons: RemainingDueButtons | null = null;
  if (remainingDueButtonsRaw) {
    try {
      remaining_due_buttons = mergeRemainingDueButtons(JSON.parse(remainingDueButtonsRaw));
    } catch {
      remaining_due_buttons = null;
    }
  }
  const registrationHeader = typeof registrationHeaderRaw === "string" ? registrationHeaderRaw.trim() : "";
  // Deliberately NOT carrying over cash_refund from the source event: refunds
  // are switched on per event, against a Chase account and refund user
  // configured for that event.
  const cashless_configurations = {
    pin_on_association_only: pinOnAssociationOnly,
    hide_from_pos_dropdown: hideFromPosDropdown,
    registration_header: registrationHeader || null,
    remaining_due_buttons,
  };
  const webCheckoutProcessorId = body.get("web_checkout_payment_processor_id") as string;
  const webCheckoutSlug = body.get("web_checkout_slug") as string;
  const webCheckoutLogo = body.get("web_checkout_logo") as string;
  const webCheckoutBackgroundUrl = body.get("web_checkout_background_url") as string;
  const webCheckoutServiceFeePercentage = body.get("web_checkout_service_fee_percentage") as string;
  const webCheckoutDescription = body.get("web_checkout_description") as string;
  body.delete("web_checkout_payment_processor_id");
  body.delete("web_checkout_slug");
  body.delete("web_checkout_logo");
  body.delete("web_checkout_background_url");
  body.delete("web_checkout_service_fee_percentage");
  body.delete("web_checkout_description");
  const web_checkout_configurations = {
    payment_processor_id: webCheckoutProcessorId || null,
    slug: webCheckoutSlug || null,
    logo: webCheckoutLogo || null,
    background_url: webCheckoutBackgroundUrl || null,
    service_fee_percentage: webCheckoutServiceFeePercentage || 0,
    description: webCheckoutDescription || null,
  };
  const tokensString = body.get("available_tokens") as string;
  const available_tokens =
    tokensString.length > 0
      ? tokensString.split(",").map((tag) => tag.trim())
      : [];
  const zonesString = body.get("location_zones") as string;
  const location_zones =
    zonesString.length > 0
      ? zonesString.split(",").map((tag) => tag.trim())
      : [];
  const tz = body.get("timezone");
  if (tz === "ADT") {
    timezonediffs = -3;
  } else if (tz === "AST") {
    timezonediffs = -4;
  } else if (tz === "EDT") {
    timezonediffs = -4;
  } else if (tz === "EST") {
    timezonediffs = -5;
  } else if (tz === "CDT") {
    timezonediffs = -5;
  } else if (tz === "CST") {
    timezonediffs = -6;
  } else if (tz === "MDT") {
    timezonediffs = -6;
  } else if (tz === "MST") {
    timezonediffs = -7;
  } else if (tz === "PDT") {
    timezonediffs = -7;
  } else if (tz === "PST") {
    timezonediffs = -8;
  } else {
    timezonediffs = 0;
  }
  const slug = body.get("slug");

  if (slug === "") {
    body.delete("slug");
  } else if (typeof slug === "string") {
    const lowerCaseSlug = slug.trim().toLowerCase();
    body.set("slug", lowerCaseSlug);
  }
  const perOrderFeeString = body.get("ticketing_service_fee_per_order") as string;
  const perOrderFeeFloat = parseFloat(perOrderFeeString) ;
  const perOrderFeeInt = (Math.round(perOrderFeeFloat * 100))
  const bodyData = Object.fromEntries(body);

  try {
    await client.mutate({
      mutation: CREATE_EVENT,
      variables: {
        id,
        input: {
          ...bodyData,
          available_tokens,
          location_zones,
          payment_types: paymentTypes,
          start_date: bodyData?.start_date || null,
          end_date: bodyData?.end_date || null,
          frontgate_site_id: bodyData?.frontgate_site_id || null,
          payment_processor_id: bodyData?.payment_processor_id || null,
          ticketing_payment_processor_id: bodyData?.ticketing_payment_processor_id || null,
          invoicing_payment_processor_id: bodyData?.invoicing_payment_processor_id || null,
          timezone_diff: timezonediffs,
          web_surcharge_percentage: bodyData?.web_surcharge_percentage || 0,
          ticketing_service_fee_per_order: perOrderFeeInt || 0,
          signup_questions,
          sub_events,
          is_asset_transfer_combine,
          ticketing_configurations,
          cashless_configurations,
          web_checkout_configurations,
          last_updated_by: user?.id,
        },
      },
    });
    toast.success("Event Cloned");
    return redirect("/events/list");
  } catch (error) {
    toast.error("Failed To Clone Event: " + error);
  }
};

export const EventClone = () => {
  const organizations = useOrganizationStore((state) => state.organizations);
  const permissions = sessionStorage.getItem("hasuraDefaultRole");
  const { event, paymentProcessors, images } = useLoaderData() as {
    event: Event;
    paymentProcessors: PaymentProcessor[];
    vendors: Vendor[];
    images: ImageLibrary[];
  };
  const imageOptions = images.map((item: { image_base64: string; display_name: string }) => ({
    value: item.image_base64,
    label: item.display_name,
    display_name: item.display_name,
    image_base64: item.image_base64,
  }));
  const defaultStartDate = event.start_date
    ? parseDate(event.start_date)
    : null;
  const defaultEndDate = event.end_date ? parseDate(event.end_date) : null;
  const [slugInputValue, setSlugInputValue] = useState(event.slug);
  const [ticketingSlugInputValue, setTicketingSlugInputValue] = useState(
    event.ticketing_slug
  );
  const [webCheckoutSlugInputValue, setWebCheckoutSlugInputValue] = useState(
    event.web_checkout_configurations?.slug ?? ""
  );
  const sortedPaymentProcessors = [...paymentProcessors]
    .filter((p) => p.is_active !== false)
    .sort((a, b) => a.payment_processor.localeCompare(b.payment_processor));
  const [logoutProtected, setLogoutProtected] = useState(
    event?.is_org_logout_protected
  );
  const clonedEventName = `${event.name} - clone`;
  const [serviceFeePerOrder, setServiceFeePerOrder] = useState<number>(event?.ticketing_service_fee_per_order / 100);
  const [serviceFeePerOrderTopUp, setServiceFeePerOrderTopUp] = useState<number>(event?.top_up_service_fee_per_order / 100);
  const [eventLogoBase64, setEventLogoBase64] = useState<string>(event?.event_logo_base64 ?? "");
  const [descriptionContent, setDescriptionContent] = useState<string>(event?.description ?? "");
  const [webCheckoutDescriptionContent, setWebCheckoutDescriptionContent] = useState<string>(event?.web_checkout_configurations?.description ?? "");
  const [remainingDueButtons, setRemainingDueButtons] = useState<RemainingDueButtons>(() =>
    mergeRemainingDueButtons(event?.cashless_configurations?.remaining_due_buttons)
  );

  const updateRemainingDueButtonField = (
    id: RemainingDueButtonId,
    field: "label" | "color",
    value: any
  ) => {
    setRemainingDueButtons((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  // See EventEdit.tsx for full rationale on the "exclude" wildcard semantics.
  const updateRemainingDueButtonPosition = (
    id: RemainingDueButtonId,
    newPosition: 1 | 2 | 3 | "exclude"
  ) => {
    setRemainingDueButtons((prev) => {
      const oldPosition = prev[id].position;
      if (oldPosition === newPosition) return prev;
      const next: RemainingDueButtons = {
        ...prev,
        [id]: { ...prev[id], position: newPosition },
      };
      if (newPosition === "exclude") return next;
      const swapId = REMAINING_DUE_BUTTON_IDS.find(
        (other) => other !== id && prev[other].position === newPosition
      );
      if (swapId) {
        next[swapId] = { ...prev[swapId], position: oldPosition };
      }
      return next;
    });
  };

  const subEventRef = useRef<SubEventManagerHandle>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [showSubEventWarning, setShowSubEventWarning] = useState(false);
  const bypassSubEventCheck = useRef(false);

  const handlePriceChange = (value: number) => {
    setServiceFeePerOrder(value);
  };
  const handlePriceChangeTopUp = (value: number) => {
    setServiceFeePerOrderTopUp(value);
  };

  const handleThumbReady = async (blob: Blob) => {
    const dataUrl = await blobToDataURL(blob);
    const rawBase64 = dataUrlToRawBase64(dataUrl);
    setEventLogoBase64(rawBase64);
  };

  async function blobToDataURL(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  }

  function dataUrlToRawBase64(dataUrl: string): string {
    const [, raw = ""] = dataUrl.split(",", 2);
    return raw;
  }

  function handleClearImage() {
    setEventLogoBase64("");
  }

  return (
    <Form
      ref={formRef}
      method="post"
      action={`/events/${event.id}/clone`}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
      onSubmit={(e) => {
        if (bypassSubEventCheck.current) {
          bypassSubEventCheck.current = false;
          return;
        }
        if (subEventRef.current?.hasUncommittedData()) {
          e.preventDefault();
          setShowSubEventWarning(true);
        }
      }}
    >
      <FormLayout>
        <div className="col-span-3">
          <Input
            label="Event Name"
            name="name"
            defaultValue={clonedEventName}
          />
        </div>
        <div className="col-span-1">
          <IndeterminateCheckbox
            label="Is Active?"
            name="is_active"
            defaultChecked={event.is_active}
          />
        </div>
        <div className="col-span-2">
          <IndeterminateCheckbox
            label="Hide from POS"
            name="cashless_hide_from_pos_dropdown"
            defaultChecked={event.cashless_configurations?.hide_from_pos_dropdown ?? false}
          />
        </div>
        {permissions === "admin" && (
          <div className="col-span-6">
            <CustomSelect
              title="Organization"
              name="organization_id"
              required
              defaultValue={event?.organization_id}
              options={organizations.map((org) => ({
                label: org.name,
                value: org.id,
              }))}
            />
          </div>
        )}
        {permissions !== "admin" && (
          <input
            type="hidden"
            value={event?.organization_id ?? ""}
            name="organization_id"
          />
        )}
        <div className="col-span-6">
          <Tab
              labels={[
                "Event Information",
                "Features",
                "Payments Configuration",
                "Cashless Configuration",
                "Ticketing Configuration",
                "Invoicing Configuration",
                "Web-Checkout Configuration",
                "Sub-Events",
              ]}
          >
            {/* Tab 1: Event Info */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-3">
                <DatePicker
                    label="Start Date"
                    name="start_date"
                    defaultValue={defaultStartDate}
                />
              </div>
              <div className="col-span-3">
                <DatePicker
                    label="End Date"
                    name="end_date"
                    defaultValue={defaultEndDate}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="Start Time"
                    name="start_time"
                    defaultValue={event.start_time}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="End Time"
                    name="end_time"
                    defaultValue={event.end_time}
                />
              </div>
              <div className="col-span-6">
                <CustomSelect
                    title="Timezone"
                    required
                    name="timezone"
                    defaultValue={event.timezone}
                    options={[
                      { value: "ADT", label: "Atlantic Daylight Time" },
                      { value: "AST", label: "Atlantic Standard Time" },
                      { value: "EDT", label: "Eastern Daylight Time" },
                      { value: "EST", label: "Eastern Standard Time" },
                      { value: "CDT", label: "Central Daylight Time" },
                      { value: "CST", label: "Central Standard Time" },
                      { value: "MDT", label: "Mountain Daylight Time" },
                      { value: "MST", label: "Mountain Standard Time" },
                      { value: "PDT", label: "Pacific Daylight Time" },
                      { value: "PST", label: "Pacific Standard Time" },
                    ]}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="Street Address"
                    name="address_street"
                    defaultValue={event.address_street}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="Street Address 2"
                    name="address_street_2"
                    defaultValue={event.address_street_2}
                />
              </div>
              <div className="col-span-3">
                <Input
                    label="City"
                    name="address_city"
                    defaultValue={event.address_city}
                />
              </div>
              <div className="col-span-1">
                <Input
                    label="State"
                    name="address_state"
                    defaultValue={event.address_state}
                />
              </div>
              <div className="col-span-2">
                <Input
                    label="Zipcode"
                    name="address_zipcode"
                    defaultValue={event.address_zipcode}
                />
              </div>
            </div>
            {/* Tab 2: Feature Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <TagsInput
                    label="Event Zones"
                    name="location_zones"
                    placeholder="Add a Zone Name (press 'enter' to add)"
                    defaultValue={event.location_zones}
                />
              </div>
              <div className="col-span-2">
                <IndeterminateCheckbox
                    label="Password Protected Organizer Logout"
                    name="is_org_logout_protected"
                    defaultChecked={event?.is_org_logout_protected}
                    onChange={(checked) => setLogoutProtected(checked as boolean)}
                />
              </div>
              <div className="col-span-4">
                <IndeterminateCheckbox
                    label="Password Protected Clerk Logout"
                    name="is_clerk_logout_protected"
                    defaultChecked={event?.is_clerk_logout_protected}
                    onChange={(checked) => setLogoutProtected(checked as boolean)}
                />
              </div>
              {eventLogoBase64 && (
                <div className="col-span-3 sm:col-span-1">
                  <div className="relative w-[125px] h-[125px]">
                    <img
                      src={`data:image/webp;base64,${eventLogoBase64}`}
                      width={125}
                      height={125}
                      alt="event logo"
                      className="rounded border border-slate-200 object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleClearImage}
                      aria-label="Remove image"
                      title="Remove image"
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#f04345] text-white
                        flex items-center justify-center shadow ring-1 ring-white/60 hover:opacity-90
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f04345]"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
              <div className="col-span-3 sm:col-span-2">
                <h4 className="block text-md font-medium text-gray-700">Event Logo (POS App)
                  <CompactThumbUploader onReady={handleThumbReady} variant={'landscape'} />
                </h4>
              </div>
              <div className="col-span-6 sm:col-span-2">
                <CustomSelect
                  title="Image from Library"
                  name="image_library"
                  value={eventLogoBase64}
                  onChange={(v) => {
                    setEventLogoBase64(v);
                  }}
                  options={imageOptions}
                />
              </div>
            </div>
            {/* Tab 3: Payment Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="payment_processor_id"
                    title="Payment Processor"
                    defaultValue={event.payment_processor_id}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <>
                <div className="col-span-6">
                  <Input
                      label="Processor Dynamic Descriptor"
                      placeholder="Max 22 Characters"
                      name="dynamic_descriptor"
                      defaultValue={event.dynamic_descriptor}
                      maxLength={22}
                      sanitize={/[^a-zA-Z0-9 ]/g}
                  />
                </div>
              </>
              <div className="col-span-6">
                <CustomSelect
                    title="Currency"
                    name="currency"
                    defaultValue={event.currency}
                    options={[{ value: "USD", label: "USD" }]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Service Fee Label"
                    name="digital_surcharge_label"
                    defaultValue={event.digital_surcharge_label}
                />
              </div>
              <div className="col-span-6 mt-4">
                <p className="block text-sm font-semibold text-gray-800 mb-1">
                  Remaining Due Section
                </p>
                <p className="block text-xs text-gray-500 mb-3">
                  Customize the buttons shown on the cashless Remaining Due
                  screen in the POS app. Position uses auto-swap — moving one
                  button to a slot pushes the previous occupant out.
                </p>
                <div className="space-y-3">
                  {REMAINING_DUE_BUTTON_IDS.map((id) => {
                    const btn = remainingDueButtons[id];
                    return (
                      <div
                        key={id}
                        className="border border-gray-200 rounded-md p-3"
                      >
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {REMAINING_DUE_BUTTON_DISPLAY_NAMES[id]}
                        </p>
                        <div className="grid grid-cols-12 gap-3">
                          <div className="col-span-12 sm:col-span-6">
                            <Input
                              label="Label"
                              name={`remaining_due_btn_${id}_label`}
                              value={btn.label}
                              onChange={(v) =>
                                updateRemainingDueButtonField(id, "label", String(v ?? ""))
                              }
                            />
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <CustomSelect
                              title="Color"
                              value={btn.color}
                              onChange={(v) =>
                                updateRemainingDueButtonField(id, "color", v)
                              }
                              options={[
                                { label: "Red", value: "red" },
                                { label: "Black", value: "black" },
                              ]}
                            />
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <CustomSelect
                              title="Position"
                              value={btn.position}
                              onChange={(v) =>
                                updateRemainingDueButtonPosition(
                                  id,
                                  v === "exclude"
                                    ? "exclude"
                                    : (Number(v) as 1 | 2 | 3)
                                )
                              }
                              options={[
                                { label: "Top", value: 1 },
                                { label: "Middle", value: 2 },
                                { label: "Bottom", value: 3 },
                                { label: "Exclude (Hide Button)", value: "exclude" },
                              ]}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <input
                  type="hidden"
                  name="cashless_remaining_due_buttons"
                  value={JSON.stringify(remainingDueButtons)}
                />
              </div>
            </div>
            {/* Tab 4: Cashless Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <Input
                    label="Frontgate Site Id"
                    name="frontgate_site_id"
                    defaultValue={event.frontgate_site_id}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Cashless URL Slug"
                    name="slug"
                    value={slugInputValue}
                    disallowUnderscore={true}
                    onChange={(value) => setSlugInputValue(value as string)}
                />
              </div>
              <div className="block text-sm font-medium text-gray-700 col-span-6">
                <a
                    href={`https://cashless.roninpos.app/events/${slugInputValue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                >
                  https://cashless.roninpos.app/events/{slugInputValue}
                </a>
                <br />
              </div>
              <div className="col-span-6">
                <Input
                    label="Custom Header"
                    name="cashless_registration_header"
                    defaultValue={event.cashless_configurations?.registration_header ?? ""}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Cashless Logo URL"
                    name="logo"
                    defaultValue={event.logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Cashless Background URL"
                    name="background_url"
                    defaultValue={event.background_url}
                />
              </div>
              <div className="col-span-6">
                <TagsInput
                    label="Eligible Token Types"
                    name="available_tokens"
                    placeholder="Add a Token Type (press 'enter' to add)"
                    defaultValue={event.available_tokens}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Open-Loop Cashless Enabled"
                    name="open_loop_cashless"
                    defaultChecked={event.open_loop_cashless}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Cashless Transfer/Combine Enabled"
                    name="is_asset_transfer_combine"
                    defaultChecked={event.is_asset_transfer_combine}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Cashless Top-Up Sales Enabled"
                    name="cashless_top_up_sales"
                    defaultChecked={event.cashless_top_up_sales}
                />
              </div>
              <div className="col-span-1">
                <IndeterminateCheckbox
                    label="PIN on First Association Only Enabled"
                    name="cashless_pin_on_association_only"
                    defaultChecked={event.cashless_configurations?.pin_on_association_only ?? false}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Require UID/PrintID on Registration Flow"
                    name="uid_reg_flow"
                    defaultChecked={event.uid_reg_flow}
                />
              </div>
              <div className="col-span-1">
                <IndeterminateCheckbox
                    label="Require Customer Details at Cash Loading (Required for Canada)"
                    name="require_cash_loading_customer_fields"
                    defaultChecked={event?.require_cash_loading_customer_fields}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    name="top_up_service_fee_percentage"
                    label="Cashless Top-Up Service Fee Value (%)"
                    defaultValue={event.top_up_service_fee_percentage}
                />
              </div>
              <div className="col-span-6">
                <PriceInput
                    label="Cashless Top-Up Service Fee Per Order"
                    name="top_up_service_fee_per_order"
                    onChange={(value) => handlePriceChangeTopUp(value ? value : 0)}
                    value={serviceFeePerOrderTopUp}
                />
              </div>
              <DemographicQuestionsBuilder defaultValue={event.signup_questions} />
            </div>
            {/* Tab 5: Ticketing */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="ticketing_payment_processor_id"
                    title="Ticketing Payment Processor"
                    defaultValue={event.ticketing_payment_processor_id}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Ticketing URL Slug"
                    name="ticketing_slug"
                    value={ticketingSlugInputValue}
                    onChange={(value) => setTicketingSlugInputValue(value as string)}
                />
              </div>
              <div className="block text-sm font-medium text-gray-700 col-span-6">
                <a
                    href={`https://ticketing.roninpos.app/events/${ticketingSlugInputValue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                >
                  https://ticketing.roninpos.app/events/{ticketingSlugInputValue}
                </a>
              </div>
              <div className="col-span-6">
                <Input
                    label="Ticketing Logo URL"
                    name="ticketing_logo"
                    defaultValue={event.ticketing_logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Ticketing Background URL"
                    name="ticketing_background_url"
                    defaultValue={event.ticketing_background_url}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    name="web_surcharge_percentage"
                    label="Ticketing Site Service Fee Value (%)"
                    defaultValue={event.web_surcharge_percentage}
                />
              </div>
              <div className="col-span-6">
                <PriceInput
                    label="Ticketing Site Service Fee Per Order"
                    name="ticketing_service_fee_per_order"
                    onChange={(value) => handlePriceChange(value ? value : 0)}
                    value={serviceFeePerOrder}
                />
              </div>
              <div className="col-span-6">
                <TicketingItemHTML
                  content={descriptionContent}
                  setContent={setDescriptionContent}
                  label="Ticketing Event Description (Shows on Ticketing Sites)"
                />
                <Input type="hidden" name="description" value={descriptionContent} />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <IndeterminateCheckbox
                    label="Tickets Remaining Call-To-Action?"
                    name="ticketing_show_remaining_cta"
                    defaultChecked={event.ticketing_configurations?.show_remaining_cta ?? true}
                />
              </div>
            </div>
            {/* Tab 6: Invoicing */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="invoicing_payment_processor_id"
                    title="Invoicing Payment Processor"
                    defaultValue={event.invoicing_payment_processor_id}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Invoicing Logo URL"
                    name="invoicing_logo"
                    defaultValue={event.invoicing_logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Invoicing Background URL"
                    name="invoicing_background_url"
                    defaultValue={event.invoicing_background_url}
                />
              </div>
            </div>
            {/* Tab 7: Web-Checkout Configuration */}
            <div className="grid grid-cols-6 gap-5">
              <div className="col-span-6">
                <CustomSelect
                    name="web_checkout_payment_processor_id"
                    title="Web-Checkout Payment Processor"
                    defaultValue={event.web_checkout_configurations?.payment_processor_id ? Number(event.web_checkout_configurations.payment_processor_id) : null}
                    options={[
                      { label: "PENDING", value: "" },
                      ...sortedPaymentProcessors.map((p) => ({
                        value: p.id,
                        label: p.payment_processor,
                      })),
                    ]}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Web-Checkout URL Slug"
                    name="web_checkout_slug"
                    value={webCheckoutSlugInputValue}
                    onChange={(value) => setWebCheckoutSlugInputValue(value as string)}
                />
              </div>
              <div className="block text-sm font-medium text-gray-700 col-span-6">
                <a
                    href={`https://web-checkout.roninpos.app/clients/${webCheckoutSlugInputValue}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline"
                >
                  https://checkout.roninpos.app/events/{webCheckoutSlugInputValue}
                </a>
              </div>
              <div className="col-span-6">
                <Input
                    label="Web-Checkout Logo URL"
                    name="web_checkout_logo"
                    defaultValue={event.web_checkout_configurations?.logo}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Web-Checkout Background URL"
                    name="web_checkout_background_url"
                    defaultValue={event.web_checkout_configurations?.background_url}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    name="web_checkout_service_fee_percentage"
                    label="Web-Checkout Service Fee Value (%)"
                    defaultValue={event.web_checkout_configurations?.service_fee_percentage ?? 0}
                />
              </div>
              <div className="col-span-6">
                <TicketingItemHTML
                    content={webCheckoutDescriptionContent}
                    setContent={setWebCheckoutDescriptionContent}
                    label="Web-Checkout Description (Shows on Web Checkout Site)"
                />
                <input type="hidden" name="web_checkout_description" value={webCheckoutDescriptionContent} />
              </div>
            </div>
            {/* Tab 8: Sub-Events */}
            <div className="grid grid-cols-6 gap-5">
              <SubEventManager ref={subEventRef} defaultValue={event.sub_events} />
            </div>
          </Tab>
        </div>
        <input
          type="hidden"
          value={eventLogoBase64}
          name="event_logo_base64"
        />
      </FormLayout>

      <Transition.Root show={showSubEventWarning} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setShowSubEventWarning}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Uncommitted Sub-Event
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have data entered in the sub-event form that hasn't been added. Would you like to go back and add it, or save without it?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => {
                        setShowSubEventWarning(false);
                        bypassSubEventCheck.current = true;
                        formRef.current?.requestSubmit();
                      }}
                    >
                      Save Without Adding
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => setShowSubEventWarning(false)}
                    >
                      Go Back
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </Form>
  );
};
